/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useEffect, useRef, useState} from "react";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import useCustomTimeslotsNew from "utils/hooks/useCustomTimeslotsNew";
import BookingAction from "store/booking/BookingAction";
import {PackageCategory} from "types/enums/PackageCategory";
import {DEFAULT_HALL_ID, IS_UPSELL_ALLOWED} from "helpers/config";
import {consoleLog} from "utils/utility";
import {RouteComponentProps} from "react-router";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";

/**
 * Shared logic for the "packages / credits" booking views
 * (Family, Events and Basic credits).
 *
 * Centralises the selectors, karting-option helpers, navigation handlers and
 * the effects that are identical across those views. The credit-initialisation
 * effect differs per view and therefore stays inside each component.
 *
 * The full result of {@link usePackageItemKartingOptions} is spread onto the
 * return value, so consumers can keep destructuring the exact helpers they need
 * (getTotalPrice, getPackageItemsPrice, setDefaultPackageCreditItems, ...).
 */
const useBookingPackagesView = (history: RouteComponentProps["history"], useCommonPackageDuration: boolean = false) => {
    const dispatch = useDispatch();
    const bookingState = useTypedSelector(state => state.bookingState);
    const {historyPush, handleRouteNavigation} = useNavigation();

    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const availableCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.availableCreditItems);
    const selectedPackageItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageItems);
    const selectedPackageCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageCreditItems);
    const availableCompetitionItems = useTypedSelector(state => state.bookingState.bookingProcess.availableCompetitionItems);
    const selectedPackageCreditItemsQuantity = selectedPackageCreditItems.reduce((acc, item) => acc + item.quantity, 0);
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const personsCount: number = filter.adults + filter.children;
    const [allFilled, setAllFilled] = useState<boolean>(false);

    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);

    const packageOptions = usePackageItemKartingOptions();
    const {setDefaultPackagesItems, manageResetStates, getFilledCount} = packageOptions;

    const {bookWithAutomaticTimeslot} = useCustomTimeslotsNew(() => {}, history, [scrollRefTimeslots, scrollRefCustomTimeslots, scrollRefConfirmTimeslots]);

    const fetchPackagesByCategoryAndTimeSlot = () => {
        dispatch(
            BookingAction.fetchPackagesByCategoryAndTimeSlot({
                adults: filter.adults,
                children: filter.children,
                date: filter.date,
                package_category_name: filter?.category ? filter.category : PackageCategory.General,
                hall_id: DEFAULT_HALL_ID,
                activity_ids: filter.activity_ids.length === 0 ? [1] : filter.activity_ids,
                timeslot_id: selectedTimeslot ? selectedTimeslot.id : null,
            })
        ).then(r => {
            consoleLog("fetchPackagesByCategoryAndTimeSlot: res", r);
        });
    };

    const onNextStepHandler = () => {
        const availablePackages = bookingState?.bookingProcess?.availablePackageItems;
        const availablePackagesHasCompetitions = Boolean(availablePackages?.competitionItems?.length || availablePackages?.includedCompetitionItems?.length);
        const availablePackagesHasUpsell = availablePackages ? availablePackages.upsells.length > 0 : false;

        if (availablePackagesHasCompetitions) {
            return historyPush(RouteName.Competitions, "Packages");
        }

        if (IS_UPSELL_ALLOWED && availablePackagesHasUpsell) {
            return historyPush(RouteName.UpsellsNew, "Packages");
        }
        bookWithAutomaticTimeslot(null, true);
    };

    // todo: ??? change what?
    const handleChange = () => {
        consoleLog("handleChange");
    };

    useEffect(() => {
        fetchPackagesByCategoryAndTimeSlot();
    }, [filter.adults, filter.children]);

    useEffect(() => {
        const hasDefaultPackageForMissingPerson = availablePackageItems && selectedPackageItems.some(item => {
            return item.id === null && (availablePackageItems.packageItems[item.person_type] || []).some(packageItem => !packageItem.karting);
        });

        if ((selectedPackageItems.length === 0 && personsCount > 0) || hasDefaultPackageForMissingPerson) {
            setDefaultPackagesItems(useCommonPackageDuration);
        }
        if (selectedPackageItems.length > 0) {
            manageResetStates(useCommonPackageDuration);
        }
    }, [availablePackageItems, selectedPackageItems]);

    useEffect(() => {
        setAllFilled(getFilledCount() == personsCount);
    }, [selectedPackageItems]);

    return {
        // state / selectors
        bookingState,
        availablePackageItems,
        availableCreditItems,
        selectedPackageItems,
        selectedPackageCreditItems,
        selectedPackageCreditItemsQuantity,
        selectedTimeslot,
        personsCount,
        allFilled,
        availableCompetitionItems,
        // navigation
        handleRouteNavigation,
        // shared handlers
        onNextStepHandler,
        handleChange,
        // karting-option helpers (getTotalPrice, getPackageItemsPrice, ...)
        ...packageOptions,
    };
};

export default useBookingPackagesView;
